<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import EmojiOrIcon from 'shared/components/EmojiOrIcon.vue';

// The courier's language: the `language` contact attribute, set by the Telegram
// bot at onboarding and editable here. Broadcasts and sami-db use it.
const props = defineProps({
  contact: {
    type: Object,
    required: true,
  },
});

const LANGUAGE_KEY = 'language';

const store = useStore();
const { t } = useI18n();
const contactAttributes = useMapGetter('attributes/getContactAttributes');
const showDropdown = ref(false);
const toggleDropdown = (value = !showDropdown.value) => {
  showDropdown.value = value;
};

const definition = computed(() =>
  (contactAttributes.value || []).find(
    attribute => attribute.attributeKey === LANGUAGE_KEY
  )
);

const language = computed(
  () =>
    props.contact.custom_attributes?.[LANGUAGE_KEY] ||
    props.contact.additional_attributes?.preferred_language ||
    ''
);

const menuItems = computed(() =>
  (definition.value?.attributeValues || []).map(value => ({
    label: value,
    value,
    action: 'select',
    isSelected: value === language.value,
  }))
);

const updateLanguage = async ({ value }) => {
  toggleDropdown(false);
  if (value === props.contact.custom_attributes?.[LANGUAGE_KEY]) return;

  try {
    await store.dispatch('contacts/update', {
      id: props.contact.id,
      customAttributes: { [LANGUAGE_KEY]: value },
    });
    useAlert(t('CONTACT_PANEL.COURIER_LANGUAGE.UPDATE_SUCCESS'));
  } catch {
    useAlert(t('CONTACT_PANEL.COURIER_LANGUAGE.UPDATE_ERROR'));
  }
};
</script>

<template>
  <div
    v-on-clickaway="() => toggleDropdown(false)"
    class="relative flex items-center w-full h-5 gap-2 ltr:-ml-1 rtl:-mr-1 text-n-slate-11"
  >
    <EmojiOrIcon
      icon="globe"
      emoji="🌐"
      icon-size="14"
      class="flex-shrink-0 ltr:ml-1 rtl:mr-1"
    />
    <button
      v-if="definition"
      type="button"
      class="text-sm truncate hover:text-n-slate-12 hover:underline"
      :title="t('CONTACT_PANEL.COURIER_LANGUAGE.TITLE')"
      @click="toggleDropdown()"
    >
      {{ language || t('CONTACT_PANEL.COURIER_LANGUAGE.SELECT') }}
    </button>
    <span
      v-else
      class="text-sm truncate"
      :title="t('CONTACT_PANEL.COURIER_LANGUAGE.TITLE')"
    >
      {{ language || t('CONTACT_PANEL.NOT_AVAILABLE') }}
    </span>
    <DropdownMenu
      v-if="showDropdown"
      :menu-items="menuItems"
      class="w-48 mt-1 top-full ltr:left-0 rtl:right-0"
      @action="updateLanguage"
    />
  </div>
</template>
